"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.WatchlistFilteringService = void 0;
const movieUtils_1 = require("../utils/movieUtils");
/**
 * Centralized service for all watchlist filtering logic
 * This ensures consistent filtering across all components
 */
class WatchlistFilteringService {
    /**
     * Merge `userMovies` docs with movies that exist only on custom watchlists
     * (no `userMovies` row yet), matching UserMovieDataContext / homepage want-to-watch counts.
     */
    static mergeCustomWatchlistOnlyMovies(userId, baseMovies, userWatchlists) {
        const map = new Map();
        for (const m of baseMovies) {
            map.set(m.movieId, m);
        }
        const now = new Date();
        for (const w of userWatchlists) {
            for (const movie of w.movieDetails || []) {
                const movieId = (0, movieUtils_1.getStandardizedMovieId)(movie);
                if (!movieId || movieId === 'unknown' || map.has(movieId))
                    continue;
                map.set(movieId, {
                    uid: userId,
                    movieId,
                    isInWatchlist: true,
                    isWatched: false,
                    isFavorite: false,
                    watchCount: 0,
                    createdAt: now,
                    updatedAt: now,
                });
            }
        }
        return map;
    }
    /**
     * Get movies that are in watchlist but not watched (want-to-watch)
     */
    static getWantToWatchMovies(movieDataCache) {
        return Array.from(movieDataCache.values())
            .filter(data => data.isInWatchlist && !data.isWatched)
            .map(data => data.movieId);
    }
    /**
     * Get movies that are marked as watched
     */
    static getWatchedMovies(movieDataCache) {
        return Array.from(movieDataCache.values())
            .filter(data => data.isWatched === true)
            .map(data => data.movieId);
    }
    /**
     * Get movies that are watched AND have reviews (not just ratings)
     */
    static getReviewedMovies(movieDataCache) {
        return Array.from(movieDataCache.values())
            .filter(data => data.isWatched === true && data.review && data.review.trim().length > 0)
            .map(data => data.movieId);
    }
    /**
     * Alias for getReviewedMovies - Get movies that are watched AND have reviews
     */
    static getYourReviewsMovies(movieDataCache) {
        return this.getReviewedMovies(movieDataCache);
    }
    /**
     * Get all movies in user's main watchlist
     */
    static getMainWatchlistMovies(movieDataCache) {
        return Array.from(movieDataCache.values())
            .filter(data => data.isInWatchlist)
            .map(data => data.movieId);
    }
    /**
     * Get movie count for any watchlist type using consistent filtering
     */
    static getMovieCount(watchlistType, movieDataCache) {
        switch (watchlistType) {
            case 'want-to-watch':
                return this.getWantToWatchMovies(movieDataCache).length;
            case 'watched':
                return this.getWatchedMovies(movieDataCache).length;
            case 'main-watchlist':
                return this.getMainWatchlistMovies(movieDataCache).length;
            default:
                return 0;
        }
    }
    /**
     * Filter movie data cache by watchlist type
     */
    static filterMoviesByType(watchlistType, movieDataCache) {
        const allMovies = Array.from(movieDataCache.values());
        switch (watchlistType) {
            case 'want-to-watch':
                return allMovies.filter(data => data.isInWatchlist && !data.isWatched);
            case 'watched':
                return allMovies.filter(data => data.isWatched === true);
            case 'main-watchlist':
                return allMovies.filter(data => data.isInWatchlist);
            default:
                return [];
        }
    }
    /**
     * Check if a movie should appear in a specific watchlist type
     */
    static shouldMovieAppearInWatchlist(movieData, watchlistType) {
        if (!movieData)
            return false;
        switch (watchlistType) {
            case 'want-to-watch':
                return movieData.isInWatchlist && !movieData.isWatched;
            case 'watched':
                return movieData.isWatched === true;
            case 'main-watchlist':
                return movieData.isInWatchlist;
            default:
                return false;
        }
    }
}
exports.WatchlistFilteringService = WatchlistFilteringService;
exports.default = WatchlistFilteringService;
